import {
  computed,
  Directive,
  effect,
  inject,
  input,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { AuthorizationService } from '../services/authorization.service';
import { Role } from '../models/Role';

/**
 * Accepted values for the {@link CanAccessDirective}:
 * - `Role[]` — the element is shown when the user has any of the given roles.
 * - `boolean` — the element is shown when the value is `true`.
 */
export type CanAccessCondition = readonly Role[] | boolean;

/**
 * Structural directive that conditionally renders its host template based on
 * either a list of roles or a boolean condition.
 *
 * @example
 * ```html
 * <!-- role based -->
 * <a *canAccess="[Role.GUTACHTER, Role.PS_ADMIN]">Aufträge</a>
 *
 * <!-- boolean -->
 * <a *canAccess="isEditable()">Bearbeiten</a>
 * ```
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[canAccess]',
})
export class CanAccessDirective {
  private readonly authorizationService = inject(AuthorizationService);
  private readonly templateRef = inject(TemplateRef<unknown>);
  private readonly viewContainerRef = inject(ViewContainerRef);

  readonly canAccess = input.required<CanAccessCondition>();

  private readonly visible = computed(() => this.resolve(this.canAccess()));

  constructor() {
    let rendered = false;

    effect(() => {
      const visible = this.visible();
      if (visible === rendered) {
        return;
      }

      rendered = visible;
      if (visible) {
        this.viewContainerRef.createEmbeddedView(this.templateRef);
      } else {
        this.viewContainerRef.clear();
      }
    });
  }

  private resolve(condition: CanAccessCondition): boolean {
    if (typeof condition === 'boolean') {
      return condition;
    }

    return this.authorizationService.hasAnyRole(...condition);
  }
}
